import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import HomeBoxPreviewModal from 'components/HomeBoxPreviewModal';
import { Loading } from 'components/loading/Loading';
import { Orders } from 'pages/orders';
import React, { useEffect, useState } from 'react';
import {
  fetchUserSubscriptionDetails,
  getSampleHomeBox,
  getStripeCustomerPortalSession,
  updateUserSubscriptionProps,
} from 'services';
import { STRIPE_DASHBOARD_LINK, formatDate, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import UserSubscriptionStatusDropdown from 'components/UserSubscriptionStatusDropdown';
import SubscriptionNotes from './Notes';
import _ from 'lodash';
import Currency from 'components/Currency/Currency';
import ChangeAddressModal from 'components/ChangeAddressModal';
import RenderInfo from 'components/RenderInfo';

const UserSubscriptionDetails = props => {
  const { subscriptionId } = props.match.params;
  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<Record<string, any>>({});
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState<boolean>(false);
  const [homeBoxPreviewLoading, setHomeBoxPreviewLoading] = useState<boolean>(false);
  const [homeBoxPreviewData, setHomeBoxPreviewData] = useState<Record<string, any>[]>([]);
  const [homeBoxPreviewModalOpen, setHomeBoxPreviewModalOpen] = useState<boolean>(false);
  const [changeAddressModalOpen, setChangeAddressModalOpen] = useState<boolean>(false);
  const [fieldsEditable, setFieldsEditable] = useState<boolean>(false);
  const [lastBoxSentAt, setLastBoxSentAt] = useState<Date | null>(null);
  const [nextBoxWillBeSentAt, setNextBoxWillBeSentAt] = useState<Date | null>(null);
  const [clientComment, setClientComment] = useState<string>('');
  const [addressComment, setAddressComment] = useState<string>('');

  const [status, setStatus] = useState<string>('');
  const { user, subscription, userLocation } = subscriptionInfo;

  const fetchSubscriptionDetailsFn = async () => {
    try {
      const response = await fetchUserSubscriptionDetails(subscriptionId);
      setSubscriptionInfo(response);
      setStatus(response.status);
      response.lastBoxSentAt && setLastBoxSentAt(moment(response.lastBoxSentAt).toDate());
      response.nextBoxWillBeSentAt && setNextBoxWillBeSentAt(moment(response.nextBoxWillBeSentAt).toDate());
      setClientComment(_.get(response,'clientComment',''));
      setAddressComment(_.get(response,'addressComment',''));
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionDetailsFn();
  }, []);

  const manageSubscription = async () => {
    setSubscriptionActionLoading(true);
    try {
      const response = await getStripeCustomerPortalSession(user.objectId);
      window.open(response.url, 'blank');
    } catch (e) {
      showError(e);
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const previewHomeBoxFn = async () => {
    setHomeBoxPreviewLoading(true);
    try {
      const response = await getSampleHomeBox(subscription.objectId, user.objectId);
      setHomeBoxPreviewData(response);
      setHomeBoxPreviewModalOpen(true);
    } catch (e) {
      showError(e);
    } finally {
      setHomeBoxPreviewLoading(false);
    }
  };

  const updateSubscriptionDetails = async () => {
    setUpdateLoading(true);
    try {
      await updateUserSubscriptionProps({
        status,
        lastBoxSentAt,
        nextBoxWillBeSentAt,
        userSubId: subscriptionId,
        clientComment,
        addressComment,
      });
      await fetchSubscriptionDetailsFn();
      setFieldsEditable(false);
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleEditClick = () => {
    if (fieldsEditable) {
      updateSubscriptionDetails();
    } else {
      setFieldsEditable(true);
    }
  };

  const getSubscriptionNotesSection = () => (
    <SubscriptionNotes
      subscriptionId={subscriptionId}
      notes={subscriptionInfo.notes}
      refetchData={fetchSubscriptionDetailsFn}
    />
  );

  const getSubscriptionOrdersSection = () => (
    <div className={styles['orders-section']}>
      <div className={styles['subheading']}>Subscription Orders</div>
      <Orders
        {...props}
        hideFilter
        customAPIParams={{
          userSubscriptionId: subscriptionId,
        }}
      />
    </div>
  );

  const getDeliveryLocationSection = () => (
    <div className={styles['delivery-location-section']}>
      <div className={styles['section-header']}>
        <div className={styles['subheading']}>Delivery Location</div>
        <ActionButton
          text={`${userLocation ? 'Change' : 'Add'} Address`}
          active={true}
          isLoading={false}
          onClick={() => setChangeAddressModalOpen(true)}
        />
      </div>
      {userLocation
        ? (
          <div className={styles['details-section']}>
            <div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Address ID</div>
                <div className={`${styles['id']} ${styles['detail']}`}>{userLocation.objectId}
                </div>
              </div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Address</div>
                <div className={`${styles['detail']} ${styles['address']}`}>
                  <div>{userLocation.address}</div>
                </div>
              </div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Town</div>
                <div className={styles['detail']}>{userLocation.town}
                </div>
              </div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Zip</div>
                <div className={styles['detail']}>{userLocation.zip}</div>
              </div>
            </div>
            <div className={'mr-4'}>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Contact Name</div>
                <div className={styles['detail']}>{userLocation.contactName}
                </div>
              </div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Contact Phone</div>
                <div className={styles['detail']}>{userLocation.contactPhone}</div>
              </div>
            </div>
          </div>
        ): (
          <div className={styles['empty-location-message']}>
            No Delivery Location selected
          </div>
        )}
    </div>
  );

  const getUserDetailsSection = () => (
    <div className={styles['user-details-section']}>
      <div className={styles['subheading']}>User Details</div>
      <div className={styles['details-section']}>
        <div className={styles['details-group']}>
          <div className={styles['label']}>User ID</div>
          <a
            href={`/users/${user.objectId}`}
            className={styles['detail']}
          >
            {user.objectId}
          </a>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Name</div>
          <div className={styles['detail']}>{user.full_name}</div>
        </div>
        {user.phone ? (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Phone</div>
            <div className={styles['detail']}>{user.phone}</div>
          </div>
        ): null}
      </div>
    </div>
  );

  const getBasicDetailsSection = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['details-section']}>
        {fieldsEditable ? (
          <div className={styles['input-group']}>
            <div className={styles['label']}>Status</div>
            <UserSubscriptionStatusDropdown
              value={status}
              onChange={setStatus}
              disabled={updateLoading}
            />
          </div>
        ): (
          <div className={styles['details-group']}>
            <div className={`${styles['status']} ${styles[subscriptionInfo.status]}`}>{subscriptionInfo.status}</div>
          </div>
        )}
        <RenderInfo
          heading='Subscription ID' 
          value={subscription.objectId} 
          link={`/subscription/${subscription.objectId}`}
        />
        <div className={styles['details-group']}>
          <div className={styles['label']}>Type</div>
          <div className={styles['detail']}>{subscription.name}</div>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Price</div>
          <div className={styles['detail']}><Currency amount={subscription.price / 100}/></div>
        </div>
        {/* <div className={styles['details-group']}>
          <div className={styles['label']}>Stripe Subscription ID</div>
          <div className={styles['detail']}>{subscriptionInfo.stripeSubscriptionId}</div>
        </div> */}
        <RenderInfo
          heading='Stripe Subscription ID' 
          value={subscriptionInfo?.stripeSubscriptionId} 
          link={`${STRIPE_DASHBOARD_LINK}/subscriptions/${subscriptionInfo?.stripeSubscriptionId}`}
        />
        <div className={styles['details-group']}>
          <div className={styles['label']}>Quantity</div>
          <div className={styles['detail']}>{subscriptionInfo.quantity || '1'}</div>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Error in last Home Box Order</div>
          <div className={styles['detail']}>{subscriptionInfo.lastHomeBoxCreationError || '-'}</div>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Cancellation Reason</div>
          <div className={styles['detail']}>{subscriptionInfo.cancellationReason || '-'}</div>
        </div>
        {fieldsEditable ? (
          <div className={styles['input-group']}>
            <div className={styles['label']}>Client Comment</div>
            <textarea
              className={'form-control'}
              value={clientComment}
              onChange={e => setClientComment(e.target.value)}
              disabled={updateLoading}
            />
          </div>
        ): (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Client Comment</div>
            <div className={styles['detail']}>{clientComment || '-'}</div>
          </div>
        )}
        {fieldsEditable ? (
          <div className={styles['input-group']}>
            <div className={styles['label']}>Address Comment</div>
            <textarea
              className={'form-control'}
              value={addressComment}
              onChange={e => setAddressComment(e.target.value)}
              disabled={updateLoading}
            />
          </div>
        ): (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Address Comment</div>
            <div className={styles['detail']}>{addressComment || '-'}</div>
          </div>
        )}
        {subscriptionInfo.currentPeriodStart ? (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Current Period Start</div>
            <div className={styles['detail']}>
              {formatDate(subscriptionInfo.currentPeriodStart)}
            </div>
          </div>
        ) : null}
        {subscriptionInfo.currentPeriodEnd ? (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Current Period End</div>
            <div className={styles['detail']}>
              {formatDate(subscriptionInfo.currentPeriodEnd)}
            </div>
          </div>
        ) : null}
      </div>
      <div className={styles['details-section']}>
        <ActionButton
          text={`${fieldsEditable ? 'Save' : 'Edit Details'}`}
          active={true}
          isLoading={false}
          onClick={handleEditClick}
          customClass={styles['edit-button']}
        />
        <ActionButton
          text={'Manage Subscription'}
          active={!subscriptionActionLoading}
          isLoading={subscriptionActionLoading}
          onClick={manageSubscription}
          customClass={styles['manage-button']}
        />
        <ActionButton
          text={'Preview Home Box'}
          active={!homeBoxPreviewLoading}
          isLoading={homeBoxPreviewLoading}
          onClick={previewHomeBoxFn}
          customClass={styles['preview-button']}
        />
        {subscriptionInfo.startDate ? (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Start Date</div>
            <div className={styles['detail']}>
              {formatDate(subscriptionInfo.startDate)}
            </div>
          </div>
        ): null}
        {subscriptionInfo.endDate ? (
          <div className={styles['details-group']}>
            <div className={styles['label']}>End Date</div>
            <div className={styles['detail']}>
              {formatDate(subscriptionInfo.endDate)}
            </div>
          </div>
        ): null}
        {fieldsEditable ? (
          <div className={styles['input-group']}>
            <div className={styles['label']}>Last Box Sent At</div>
            <div className={styles['date-picker']}>
              <DatePicker
                className={'form-control'}
                disabled={updateLoading}
                popperPlacement={'left'}
                selected={lastBoxSentAt ? moment(lastBoxSentAt).toDate() : null}
                onChange={(val: any) => setLastBoxSentAt(val)}
              />
            </div>
          </div>
        ): (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Last Box Sent At</div>
            <div className={styles['detail']}>
              {subscriptionInfo.lastBoxSentAt ? formatDate(subscriptionInfo.lastBoxSentAt) : '-'}
            </div>
          </div>
        )}
        {fieldsEditable ? (
          <div className={styles['input-group']}>
            <div className={styles['label']}>Next Box</div>
            <div className={styles['date-picker']}>
              <DatePicker
                className={'form-control'}
                disabled={updateLoading}
                popperPlacement={'left'}
                selected={nextBoxWillBeSentAt ? moment(nextBoxWillBeSentAt).toDate() : null}
                onChange={(val: any) => setNextBoxWillBeSentAt(val)}
              />
            </div>
          </div>
        ): (
          <div className={styles['details-group']}>
            <div className={styles['label']}>Next Box</div>
            <div className={styles['detail']}>
              {subscriptionInfo.nextBoxWillBeSentAt ? formatDate(subscriptionInfo.nextBoxWillBeSentAt) : '-'}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const onSubscriptionAddressChange = async(selectedAddress) => {
    setLoading(true);
    const payload = {
      userSubId: subscriptionId,
      userLocationId: selectedAddress.objectId,
      addressComment: '',
      clientComment: '',
    };
    try {
      await updateUserSubscriptionProps(payload);
      await fetchSubscriptionDetailsFn();
      showSuccess('Address updated successfully');
      setChangeAddressModalOpen(false);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  if (loading) return <Loading />;

  return (
    <>
      {homeBoxPreviewModalOpen && (
        <HomeBoxPreviewModal
          previewData={homeBoxPreviewData}
          toggleModal={() => setHomeBoxPreviewModalOpen(!homeBoxPreviewModalOpen)}
        />
      )}
      {changeAddressModalOpen && (
        <ChangeAddressModal
          userId={user.objectId}
          subscriptionId={subscriptionId}
          toggleModal={() => setChangeAddressModalOpen(!changeAddressModalOpen)}
          onAddressChange={onSubscriptionAddressChange}
          title="Change Subscription Address"
        />
      )}
      <div className={styles['main-container']}>
        <div className={styles['header']}>
          <div className={styles['heading']}>User Subscription Details</div>
          <BackButton />
        </div>
        {getBasicDetailsSection()}
        {getUserDetailsSection()}
        {getDeliveryLocationSection()}
        {getSubscriptionOrdersSection()}
        {getSubscriptionNotesSection()}
      </div>
    </>
  );
};

export { UserSubscriptionDetails };
